/**
 * KCIG Theme JavaScript
 *
 * Main entry point for theme JavaScript and CSS imports.
 */

// DOM Ready
document.addEventListener("DOMContentLoaded", () => {
  initMobileMenu();
  initDesktopDropdowns();
  initMobileAccordions();
  initSmoothScroll();
  initAnimations();
  initVideoModals();
  initDownloadLinks();
});

/**
 * Mobile Menu Toggle
 */
function initMobileMenu() {
  const toggle = document.querySelector("[data-menu-toggle]");
  const menu = document.querySelector("[data-mobile-menu]");

  if (!toggle || !menu) return;

  toggle.addEventListener("click", () => {
    const isExpanded = toggle.getAttribute("aria-expanded") === "true";
    toggle.setAttribute("aria-expanded", !isExpanded);
    menu.hidden = isExpanded;
    document.body.classList.toggle("mobile-menu-open", !isExpanded);
  });

  // Close on Escape
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && !menu.hidden) {
      toggle.setAttribute("aria-expanded", "false");
      menu.hidden = true;
      document.body.classList.remove("mobile-menu-open");
      toggle.focus();
    }
  });
}

/**
 * Desktop Dropdown Menus
 */
function initDesktopDropdowns() {
  const dropdownItems = document.querySelectorAll(
    ".header-nav__item--dropdown",
  );

  dropdownItems.forEach((item) => {
    const btn = item.querySelector("[data-dropdown]");
    const menu = item.querySelector(".header-dropdown");

    if (!btn || !menu) return;

    // Toggle on click
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      const isOpen = btn.getAttribute("aria-expanded") === "true";

      // Close all other dropdowns first
      closeAllDropdowns();

      if (!isOpen) {
        btn.setAttribute("aria-expanded", "true");
        menu.classList.add("is-open");
      }
    });

    // Open on hover (desktop only)
    let closeTimeout;
    item.addEventListener("mouseenter", () => {
      clearTimeout(closeTimeout);
      closeAllDropdowns();
      btn.setAttribute("aria-expanded", "true");
      menu.classList.add("is-open");
    });

    // Close on mouse leave (with delay for usability)
    item.addEventListener("mouseleave", () => {
      closeTimeout = setTimeout(() => {
        btn.setAttribute("aria-expanded", "false");
        menu.classList.remove("is-open");
      }, 200);
    });
  });

  // Close dropdowns when clicking outside
  document.addEventListener("click", closeAllDropdowns);

  // Close on Escape
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      closeAllDropdowns();
    }
  });
}

function closeAllDropdowns() {
  document.querySelectorAll(".header-nav__item--dropdown").forEach((item) => {
    const btn = item.querySelector("[data-dropdown]");
    const menu = item.querySelector(".header-dropdown");
    if (btn) btn.setAttribute("aria-expanded", "false");
    if (menu) menu.classList.remove("is-open");
  });
}

/**
 * Mobile Accordion Sub-menus
 */
function initMobileAccordions() {
  const toggles = document.querySelectorAll("[data-mobile-accordion]");

  toggles.forEach((toggle) => {
    toggle.addEventListener("click", () => {
      const isExpanded = toggle.getAttribute("aria-expanded") === "true";
      const sub = toggle.nextElementSibling;

      // Close all other accordions
      toggles.forEach((other) => {
        if (other !== toggle) {
          other.setAttribute("aria-expanded", "false");
          if (other.nextElementSibling) other.nextElementSibling.hidden = true;
        }
      });

      // Toggle current
      toggle.setAttribute("aria-expanded", !isExpanded);
      if (sub) sub.hidden = isExpanded;
    });
  });
}

/**
 * Smooth Scroll for Anchor Links
 */
function initSmoothScroll() {
  document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
    anchor.addEventListener("click", function (e) {
      const href = this.getAttribute("href");
      if (href === "#") return;

      const target = document.querySelector(href);
      if (!target) return;

      e.preventDefault();

      target.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });

      history.pushState(null, null, href);
    });
  });
}

/**
 * Scroll-triggered Animations
 */
function initAnimations() {
  const prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;
  if (prefersReducedMotion) return;

  const animatedElements = document.querySelectorAll("[data-animate]");
  if (!animatedElements.length) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("animate-in");
          observer.unobserve(entry.target);
        }
      });
    },
    {
      threshold: 0.1,
      rootMargin: "0px 0px -50px 0px",
    },
  );

  animatedElements.forEach((el) => observer.observe(el));
}

/**
 * Video Block – Vimeo Modal
 *
 * Clicking a .video-block element opens a fullscreen modal
 * with the Vimeo player iframe. Closes on backdrop click,
 * close button, or Escape key.
 */
function initVideoModals() {
  const blocks = document.querySelectorAll(".video-block[data-vimeo-id]");
  if (!blocks.length) return;

  blocks.forEach((block) => {
    block.addEventListener("click", () => openVideoModal(block));
    block.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        openVideoModal(block);
      }
    });
  });
}

function openVideoModal(triggerEl) {
  const vimeoId = triggerEl.getAttribute("data-vimeo-id");
  if (!vimeoId) return;

  // Build modal DOM
  const modal = document.createElement("div");
  modal.className = "video-modal";
  modal.setAttribute("role", "dialog");
  modal.setAttribute("aria-modal", "true");
  modal.setAttribute("aria-label", "Video player");

  modal.innerHTML =
    '<button class="video-modal__close" aria-label="Close video">' +
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">' +
    '<line x1="18" y1="6" x2="6" y2="18"/>' +
    '<line x1="6" y1="6" x2="18" y2="18"/>' +
    "</svg>" +
    "</button>" +
    '<div class="video-modal__inner">' +
    '<iframe src="https://player.vimeo.com/video/' +
    encodeURIComponent(vimeoId) +
    '?autoplay=1&byline=0&portrait=0&title=0" ' +
    'allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>' +
    "</div>";

  document.body.appendChild(modal);
  document.body.style.overflow = "hidden";

  const closeBtn = modal.querySelector(".video-modal__close");
  closeBtn.focus();

  // ---- Close handlers ----

  function closeModal() {
    modal.classList.add("is-closing");
    modal.addEventListener(
      "animationend",
      () => {
        modal.remove();
        document.body.style.overflow = "";
        triggerEl.focus();
      },
      { once: true },
    );
  }

  // Close button
  closeBtn.addEventListener("click", closeModal);

  // Backdrop click (only if clicking the modal itself, not the iframe area)
  modal.addEventListener("click", (e) => {
    if (e.target === modal) {
      closeModal();
    }
  });

  // Escape key
  function onKeydown(e) {
    if (e.key === "Escape") {
      closeModal();
      document.removeEventListener("keydown", onKeydown);
    }
  }
  document.addEventListener("keydown", onKeydown);
}

/**
 * Force-download links for .is-style-download buttons.
 *
 * Uses fetch + blob so the browser triggers a save dialog instead of
 * navigating to the file — works for same-origin and CDN URLs alike.
 */
function initDownloadLinks() {
  const selector = ".is-style-download .wp-block-button__link";
  const links = document.querySelectorAll(selector);
  if (!links.length) return;

  links.forEach((link) => {
    link.addEventListener("click", (e) => {
      e.preventDefault();
      const url = link.href;
      const filename = url.split("/").pop().split("?")[0] || "download";

      fetch(url)
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.blob();
        })
        .then((blob) => {
          const a = document.createElement("a");
          a.href = URL.createObjectURL(blob);
          a.download = filename;
          a.click();
          URL.revokeObjectURL(a.href);
        })
        .catch(() => {
          window.open(url, "_blank");
        });
    });
  });
}

export {
  initMobileMenu,
  initDesktopDropdowns,
  initMobileAccordions,
  initSmoothScroll,
  initAnimations,
  initVideoModals,
  initDownloadLinks,
};
